import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { api, ApiError } from "../api";
import type { useMobileNavigation } from "./navigation";
import type { LobbyCategoryId, LobbyGame, MobileRoute } from "./types";

type GameSession = {
  game: LobbyGame;
  category: LobbyCategoryId;
  owner: string;
  status: "connecting" | "ready" | "error";
  url?: string;
  error?: string;
};
type Source = { route: MobileRoute; params: URLSearchParams; key: string; scrollY: number };

function embeddedUrl(value: unknown) {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    // Keep third-party scripts on their own origin and preserve HTTPS embedding.
    if (url.protocol !== "https:" || url.username || url.password || url.origin === window.location.origin) return null;
    return url.toString();
  } catch { return null; }
}

export function useGameLaunch({ navigation, userId, identityVersion, onSessionExpired }: {
  navigation: ReturnType<typeof useMobileNavigation>;
  userId?: string;
  identityVersion: RefObject<number>;
  onSessionExpired: () => void;
}) {
  const [activeGame, setActiveGame] = useState<GameSession | null>(null);
  const currentGame = useRef<GameSession | null>(null);
  const source = useRef<Source | null>(null);
  const controller = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const closing = useRef(false);
  const { navigate, route: pageRoute, key: pageKey } = navigation;

  const close = useCallback((returnToLobby = true) => {
    generation.current += 1;
    controller.current?.abort();
    controller.current = null;
    currentGame.current = null;
    setActiveGame(null);
    if (returnToLobby && window.location.hash === "#playing") {
      closing.current = true;
      const previous = source.current;
      // A supplier iframe can add its own history entries. Restore the host
      // directly rather than assuming one browser back exits the game.
      navigate(previous?.route ?? "games", previous ? Object.fromEntries(previous.params) : undefined, { replace: true });
    }
  }, [navigate]);
  const closePlayer = useCallback(() => close(true), [close]);
  const closeForNavigation = useCallback(() => close(false), [close]);

  useEffect(() => {
    if (pageRoute === "playing") return;
    if (currentGame.current) close(false);
    const previous = source.current;
    source.current = null;
    closing.current = false;
    if (previous && previous.key === pageKey) {
      const frame = window.requestAnimationFrame(() => window.scrollTo({ top: previous.scrollY, behavior: "instant" }));
      return () => window.cancelAnimationFrame(frame);
    }
  }, [pageRoute, pageKey, close]);

  useEffect(() => {
    if (currentGame.current && currentGame.current.owner !== userId) close();
  }, [userId, close]);
  useEffect(() => () => { generation.current += 1; controller.current?.abort(); }, []);

  const connect = useCallback(async (gameSession: GameSession) => {
    controller.current?.abort();
    const request = ++generation.current;
    const identity = identityVersion.current;
    const abort = new AbortController();
    controller.current = abort;
    const connecting: GameSession = { ...gameSession, status: "connecting", url: undefined, error: undefined };
    currentGame.current = connecting;
    setActiveGame(connecting);
    const isCurrent = () => request === generation.current && identity === identityVersion.current;
    try {
      const { game, category } = gameSession;
      const result = await api<{ url: string }>("/api/user/lobby-games/launch", {
        method: "POST",
        signal: AbortSignal.any([abort.signal, AbortSignal.timeout(25000)]),
        body: JSON.stringify({ category, provider: game.provider, gameType: game.gameType, gameCode: game.gameCode, isMobile: window.matchMedia("(max-width: 820px)").matches }),
      });
      if (!isCurrent()) return;
      const url = embeddedUrl(result.url);
      if (!url) throw new Error("该场馆暂不支持本站内打开，请选择其他游戏或联系客服。");
      const ready: GameSession = { ...connecting, status: "ready", url };
      currentGame.current = ready;
      setActiveGame(ready);
    } catch (reason) {
      if (!isCurrent()) return;
      if (reason instanceof ApiError && reason.status === 401) {
        onSessionExpired();
        close();
        return;
      }
      const error = reason instanceof ApiError ? reason.message : reason instanceof Error && reason.name !== "TimeoutError" && reason.name !== "AbortError" ? reason.message : "游戏连接超时，请稍后重新连接。";
      const failed: GameSession = { ...connecting, status: "error", error };
      currentGame.current = failed;
      setActiveGame(failed);
    } finally {
      if (request === generation.current) controller.current = null;
    }
  }, [close, identityVersion, onSessionExpired]);

  function launch(game: LobbyGame, category: LobbyCategoryId) {
    if (!userId || currentGame.current || closing.current) return;
    source.current = { route: navigation.route, params: navigation.params, key: navigation.key, scrollY: window.scrollY };
    const next: GameSession = { game, category, owner: userId, status: "connecting" };
    currentGame.current = next;
    setActiveGame(next);
    navigate("playing");
    void connect(next);
  }
  const retry = useCallback(() => {
    if (currentGame.current && currentGame.current.status !== "connecting") void connect(currentGame.current);
  }, [connect]);

  return {
    activeGame, launch, close: closePlayer, closeForNavigation, retry,
    launching: activeGame?.status === "connecting" ? activeGame.game.id : null,
    // Keep the originating lobby mounted while its game dialog is open.
    route: pageRoute === "playing" && source.current ? source.current.route : pageRoute,
    params: pageRoute === "playing" && source.current ? source.current.params : navigation.params,
  };
}
